import { useState, useCallback, useRef, useEffect } from 'react';
import type { IncidentSeverity } from '../../types/canvision.types';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const STORAGE_KEY = 'canvision:incident-sound-enabled';

/**
 * Tone configuration per severity level.
 *
 * - critical : rapid triple-beep at 880 Hz  (urgent alarm feel)
 * - high     : double tone at 660 Hz        (attention-grabbing)
 * - medium   : single tone at 440 Hz        (standard notice)
 * - low      : soft chime at 330 Hz         (gentle notification)
 */
interface ToneConfig {
  frequency: number;
  /** Duration of each beep in ms */
  duration: number;
  /** Number of beep repetitions */
  repeat: number;
  /** Oscillator waveform type */
  type: OscillatorType;
  /** Peak gain (volume). 0-1 range. */
  gain: number;
  /** Gap between repeated beeps in ms */
  gap: number;
}

const SEVERITY_TONES: Record<IncidentSeverity, ToneConfig> = {
  critical: { frequency: 880, duration: 120, repeat: 3, type: 'square', gain: 0.35, gap: 80 },
  high:     { frequency: 660, duration: 200, repeat: 2, type: 'sine',   gain: 0.30, gap: 100 },
  medium:   { frequency: 440, duration: 300, repeat: 1, type: 'sine',   gain: 0.25, gap: 0 },
  low:      { frequency: 330, duration: 350, repeat: 1, type: 'triangle', gain: 0.20, gap: 0 },
};

// ---------------------------------------------------------------------------
// Hook return type
// ---------------------------------------------------------------------------

export interface SoundManagerControls {
  /** Play the notification tone for the given severity. No-op when muted. */
  playSound: (severity: IncidentSeverity) => void;
  /** Whether sounds are currently muted. */
  isMuted: boolean;
  /** Toggle mute state. Persists choice to localStorage. */
  toggleMute: () => void;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Read persisted preference. Defaults to *enabled* (not muted). */
function readPersistedMute(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) === 'false';
  } catch {
    return false; // default: not muted
  }
}

/** Persist the enabled/disabled preference. */
function persistMute(muted: boolean): void {
  try {
    // Store the *enabled* value so 'true' = enabled, 'false' = muted
    localStorage.setItem(STORAGE_KEY, String(!muted));
  } catch {
    // localStorage unavailable -- ignore silently
  }
}

// ---------------------------------------------------------------------------
// Core sound playback (pure function, no React dependency)
// ---------------------------------------------------------------------------

/**
 * Play an oscillator-based tone using the Web Audio API.
 *
 * The AudioContext is created lazily and reused across calls.  The first call
 * **must** originate from a user-gesture callstack (click / keydown) for
 * browsers to allow audio playback.
 */
function playTone(
  ctxRef: React.MutableRefObject<AudioContext | null>,
  severity: IncidentSeverity,
): void {
  try {
    // Lazy initialisation -- the AudioContext is only created on the first
    // actual playback request, which always traces back to a user interaction
    // (e.g. the socket callback that fires after the user has already
    // interacted with the page).
    if (!ctxRef.current) {
      ctxRef.current = new AudioContext();
    }

    const ctx = ctxRef.current;

    // If the context was suspended (autoplay policy), try to resume
    if (ctx.state === 'suspended') {
      ctx.resume().catch(() => {});
    }

    const config = SEVERITY_TONES[severity] ?? SEVERITY_TONES.low;

    for (let i = 0; i < config.repeat; i++) {
      const osc = ctx.createOscillator();
      const gainNode = ctx.createGain();
      osc.connect(gainNode);
      gainNode.connect(ctx.destination);

      osc.type = config.type;
      osc.frequency.value = config.frequency;

      const startTime = ctx.currentTime + i * (config.duration + config.gap) / 1000;
      const endTime = startTime + config.duration / 1000;

      // Envelope: attack -> sustain -> exponential decay
      gainNode.gain.setValueAtTime(config.gain, startTime);
      gainNode.gain.exponentialRampToValueAtTime(0.001, endTime);

      osc.start(startTime);
      osc.stop(endTime);
    }
  } catch {
    // Web Audio API not supported or blocked -- fail silently
  }
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * `useSoundManager` -- reusable hook for incident notification sounds.
 *
 * Usage:
 * ```tsx
 * const { playSound, isMuted, toggleMute } = useSoundManager();
 *
 * // In a socket callback:
 * playSound('critical');
 *
 * // In the toolbar:
 * <IconButton onClick={toggleMute}>
 *   {isMuted ? <VolumeOffIcon /> : <VolumeUpIcon />}
 * </IconButton>
 * ```
 */
export function useSoundManager(): SoundManagerControls {
  // Mute state -- initialised from localStorage
  const [isMuted, setIsMuted] = useState<boolean>(readPersistedMute);

  // Keep a ref in sync so callbacks that close over stale state still work
  const mutedRef = useRef(isMuted);
  useEffect(() => {
    mutedRef.current = isMuted;
  }, [isMuted]);

  // Shared AudioContext -- created lazily on first playSound call
  const audioCtxRef = useRef<AudioContext | null>(null);

  // ---- playSound ----------------------------------------------------
  const playSound = useCallback((severity: IncidentSeverity): void => {
    if (mutedRef.current) return;
    playTone(audioCtxRef, severity);
  }, []);

  // ---- toggleMute ---------------------------------------------------
  const toggleMute = useCallback((): void => {
    setIsMuted((prev) => {
      const next = !prev;
      persistMute(next);
      return next;
    });
  }, []);

  // Cleanup: close the AudioContext when the component unmounts
  useEffect(() => {
    return () => {
      if (audioCtxRef.current) {
        audioCtxRef.current.close().catch(() => {});
        audioCtxRef.current = null;
      }
    };
  }, []);

  return { playSound, isMuted, toggleMute };
}

export default useSoundManager;
